import React from 'react';

import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import { theme } from '@garnish/constants';
import {
  LoyaltyChallengeFooterCompleted,
  LoyaltyChallengeFooterNew,
  LoyaltyChallengeFooterOngoing,
  LoyaltyChallengeBadge,
  LoyaltyChallengeContent,
  LoyaltyChallengeImage,
  LoyaltyChallengeTerms,
} from './subcomponents';
import type {
  ColoringConfigMap,
  LoyaltyChallengeCardBaseProps,
  LoyaltyChallengeCardCompletedProps,
  LoyaltyChallengeCardNewProps,
  LoyaltyChallengeCardOngoingProps,
  LoyaltyChallengeCardProps,
} from './LoyaltyChallengeCard.types';
import { LoyaltyChallengeCardVariants } from './LoyaltyChallengeCard.constants';

export const LoyaltyChallengeCard = (props: LoyaltyChallengeCardProps) => {
  const { status } = props;

  if (status === 'New') return <LoyaltyChallengeCardNew {...props} />;

  if (status === 'Ongoing') return <LoyaltyChallengeCardOngoing {...props} />;

  if (status === 'Completed')
    return <LoyaltyChallengeCardCompleted {...props} />;

  return null;
};

// -------------------- SUBCOMPONENTS --------------------

const LoyaltyChallengeCardBase = (props: LoyaltyChallengeCardBaseProps) => {
  const {
    variant,
    newBadgeText,
    deadlinePrefixText,
    title,
    children,
    expirationDate,
    testID,
  } = props;

  const cardStyles = coloringMap[variant];
  const shouldDisplayBadge = variant === 'New';

  return (
    <View testID={testID} style={[styles.cardContainer, cardStyles]}>
      <View style={styles.innerContainer}>
        {shouldDisplayBadge ? (
          <LoyaltyChallengeBadge label={newBadgeText} />
        ) : null}

        <LoyaltyChallengeImage variant={variant} testID={`${testID}-image`} />

        <LoyaltyChallengeContent
          expirationDate={expirationDate}
          title={title}
          deadlinePrefixText={deadlinePrefixText}
        >
          {children}
        </LoyaltyChallengeContent>
      </View>
    </View>
  );
};

// ─── VARIATIONS ─────────────────────────────────────────────────────────────────

const LoyaltyChallengeCardNew = (props: LoyaltyChallengeCardNewProps) => {
  const { onStartChallenge, disabled, isLoading, ...challengeCardProps } =
    props;

  return (
    <LoyaltyChallengeCardBase
      variant={LoyaltyChallengeCardVariants.New}
      {...challengeCardProps}
    >
      <View style={styles.childrenWrapper}>
        <LoyaltyChallengeTerms
          {...challengeCardProps}
          testID={`${props.testID}-content`}
        />
      </View>

      <LoyaltyChallengeFooterNew
        testID={`${props.testID}-footer`}
        onPress={onStartChallenge}
        disabled={disabled}
        isLoading={isLoading}
      />
    </LoyaltyChallengeCardBase>
  );
};

const LoyaltyChallengeCardOngoing = (
  props: LoyaltyChallengeCardOngoingProps,
) => {
  const {
    totalSteps,
    completedSteps,
    progressCopy,
    footerText,
    ...challengeCardProps
  } = props;

  return (
    <LoyaltyChallengeCardBase
      variant={LoyaltyChallengeCardVariants.Ongoing}
      {...challengeCardProps}
    >
      <View style={styles.childrenWrapper}>
        <LoyaltyChallengeTerms
          {...challengeCardProps}
          testID={`${props.testID}-content`}
        />
      </View>

      <LoyaltyChallengeFooterOngoing
        testID={`${props.testID}-footer`}
        totalSteps={totalSteps}
        progressCopy={progressCopy}
        completedSteps={completedSteps}
        footerText={footerText}
      />
    </LoyaltyChallengeCardBase>
  );
};

const LoyaltyChallengeCardCompleted = (
  props: LoyaltyChallengeCardCompletedProps,
) => {
  return (
    <LoyaltyChallengeCardBase
      variant={LoyaltyChallengeCardVariants.Completed}
      {...props}
    >
      <View style={styles.childrenWrapper}>
        <LoyaltyChallengeTerms {...props} testID={`${props.testID}-content`} />
      </View>

      <LoyaltyChallengeFooterCompleted
        completionText={props.completionText}
        testID={`${props.testID}-footer`}
      />
    </LoyaltyChallengeCardBase>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const coloringMap: ColoringConfigMap = {
  New: {
    backgroundColor: theme.colors.QUINOA,
  },
  Completed: {
    backgroundColor: theme.colors.CUCUMBER,
  },
  Ongoing: {
    backgroundColor: theme.colors.QUINOA,
  },
};

// ─── STYLES ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  childrenWrapper: { flex: 1 },
  cardContainer: {
    flex: 1,
    flexDirection: 'row',
    width: 310,
    paddingVertical: theme.spacing.large,
    paddingHorizontal: theme.spacing.medium,
    borderWidth: 1,
    borderColor: theme.colors.OPACITY.TRANSPARENT,
    borderRadius: theme.radius.large,
  },
  innerContainer: {
    flex: 1,
  },
});
